import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Calendar, FileCheck, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { workingProcess } from '../data/mock';

const iconMap = {
  '01': Phone,
  '02': Calendar,
  '03': FileCheck,
  '04': Send
};

export const WorkingMethodPage = () => {
  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Unsere Arbeitsweise</h1>
            <p className="text-lg text-gray-300">
              Transparent, strukturiert und effizient – so arbeiten wir. Von der ersten Kontaktaufnahme 
              bis zur Übergabe Ihres Gutachtens begleiten wir Sie professionell durch jeden Schritt.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {workingProcess.map((step, index) => {
              const IconComponent = iconMap[step.step];
              const isLast = index === workingProcess.length - 1;
              
              return (
                <div key={step.id} className="relative">
                  <div className="grid lg:grid-cols-12 gap-8 items-start">
                    {/* Step Number & Icon */}
                    <div className="lg:col-span-2 flex flex-col items-center lg:items-start">
                      <div className="relative">
                        <div className="w-20 h-20 bg-forest-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                          <IconComponent className="h-10 w-10 text-white" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-10 h-10 bg-white border-4 border-forest-600 rounded-full flex items-center justify-center">
                          <span className="text-forest-700 font-bold text-sm">{step.step}</span>
                        </div>
                      </div>
                      
                      {/* Connector Line */}
                      {!isLast && (
                        <div className="hidden lg:block w-1 h-full bg-forest-200 ml-10 mt-4"></div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-10">
                      <Card className="hover:shadow-xl transition-all border-l-4 border-l-forest-600">
                        <CardContent className="pt-6 pb-6">
                          <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                          <p className="text-gray-600 leading-relaxed text-lg">{step.description}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Qualität in jedem Detail
              </h2>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                Unsere Arbeitsweise basiert auf jahrelanger Erfahrung und bewährten Prozessen. 
                Jedes Gutachten durchläuft eine interne Qualitätskontrolle, bevor es an Sie übergeben wird.
              </p>

              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-slate-900 mb-2 text-lg">Modernste Ausrüstung</h3>
                  <p className="text-gray-600">
                    Professionelle Messtechnik, Hochauflösungskameras und aktuelle Bewertungssoftware 
                    gewährleisten präzise Ergebnisse.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-slate-900 mb-2 text-lg">Strukturierte Dokumentation</h3>
                  <p className="text-gray-600">
                    Jeder Schaden wird systematisch erfasst, fotografiert und nach einheitlichen 
                    Standards dokumentiert.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-slate-900 mb-2 text-lg">Regelmäßige Weiterbildung</h3>
                  <p className="text-gray-600">
                    Unsere Sachverständigen nehmen regelmäßig an Fortbildungen teil, um stets auf dem 
                    neuesten Stand der Technik zu sein.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/Rücken.jpeg"
                  alt="Professionelle Arbeitsweise - Gutachter bei der Fahrzeuginspektion"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Warum Sie uns wählen sollten
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center hover:shadow-lg transition-shadow border-t-4 border-t-forest-600">
              <CardContent className="pt-8 pb-8">
                <div className="text-3xl font-bold text-forest-700 mb-2">Präzise</div>
                <p className="text-gray-600">Detaillierte Schadensdokumentation mit modernster Messtechnik</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow border-t-4 border-t-forest-600">
              <CardContent className="pt-8 pb-8">
                <div className="text-3xl font-bold text-forest-700 mb-2">Unabhängig</div>
                <p className="text-gray-600">Neutrale Bewertung ohne Interessenkonflikte</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow border-t-4 border-t-forest-600">
              <CardContent className="pt-8 pb-8">
                <div className="text-3xl font-bold text-forest-700 mb-2">Schnell</div>
                <p className="text-gray-600">Express-Bearbeitung und zügige Lieferung</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow border-t-4 border-t-forest-600">
              <CardContent className="pt-8 pb-8">
                <div className="text-3xl font-bold text-forest-700 mb-2">Erfahren</div>
                <p className="text-gray-600">Qualifizierte Sachverständige mit umfassender Expertise</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-forest-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Bereit für Ihr Gutachten?</h2>
          <p className="text-lg text-forest-100 mb-8">
            Starten Sie jetzt mit Schritt 1: Kontaktieren Sie uns für eine kostenlose Erstberatung.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-white text-forest-700 hover:bg-gray-100">
              Jetzt Termin vereinbaren
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
